import { useMemo, useEffect, useState, useCallback, useReducer } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { SetupCard } from '../SetupCard'
import { IntroPage } from './IntroPage'
import { LBAIntroPage } from './LBAIntroPage'
import { LBAInterestConfirmationPage } from './LBAInterestConfirmationPage'
import { NoLBAIntroPage } from './NoLBAIntroPage'
import { PlaidIntroPage } from './PlaidIntroPage'
import {
  StatementStatus,
  fetchFinancialAccountsIfNeeded,
  getAccountUploadStatuses,
  ACCOUNT_UPLOAD_STATUSES_KEY,
  StatementStatusType,
} from '@/actions/financialAccountActions'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  fetchUserDocuments,
  markStatementMonthAsNotExpected,
} from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { selectUserStatements } from '@/features/user/your-practice/documents/lib/userDocuments.selector'
import { fetchPlaidItemsIfNeeded } from '@/actions/plaidItemActions'
import { useReselector, useToggle } from '@/utils/sharedHooks'
import { selectPlaidItems } from '@/selectors/financeSelectors'
import { NoLBAInstitutionCard } from './NoLBAInstitutionCard'
import { ReviewStatements } from './ReviewStatements'
import { ONBOARDING_V2_ROUTE_NAMES, OnboardingTaskIdents } from '../config'
import { UploadPrevMonthStatementStatus } from '@/features/user/dashboard/lib/userActionItemStatuses'
import { markUserActionItemCompleteIfExists } from '@/features/user/dashboard/lib/service'
import { useAnalyticsTrack, useAnalyticsView } from '@/features/amplitude'
import { SkipUploadStatementsModal } from './SkipUploadStatementsModal'
import { getCurrentUser } from '@/selectors/user.selectors'
import { updateFinancialProfile } from '@/actions/financialProfileActions'
import { isNil } from 'lodash'
import { getIsFetching } from '@/reducers/fetch'
import { NotExpectedState, NotExpectedStateAction } from './types'
import { useCompleteOnboardingStepIfNeeded } from '@/features/onboarding/user-onboarding-steps/onboarding.hooks'
import { GettingStartedOnboardingStep } from '@/features/onboarding/user-onboarding-steps/onboarding.reducer'

const SKIPPABLE_STEPS = ['unsupportedInstitutions', 'noLbaIntro']

export const UploadStatementsStage = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const track = useAnalyticsTrack()
  const pageView = useAnalyticsView()
  const [searchParams] = useSearchParams()

  const [showSkipButton, setShowSkipButton] = useState(false)
  const [showSkipModal, toggleSkipModal] = useToggle(false)
  const [continueDisabledMessage, setContinueDisabledMessage] = useState('')
  const [statuses, setStatuses] = useState<StatementStatus[]>()
  const currentUser = useReselector(getCurrentUser)
  const [isInterestedInLba, setIsInterestedInLba] = useState(
    currentUser?.financialProfile?.limitedBankAccessInterest
  )
  const location = useLocation()
  const fromGettingStarted = location.state?.fromGettingStarted || false

  const [notExpectedState, setNotExpectedValue] = useReducer(
    (
      state: NotExpectedState,
      { financialAccountId, monthYear, value }: NotExpectedStateAction
    ) => {
      return {
        ...state,
        [financialAccountId]: {
          ...(state[financialAccountId] || {}),
          [monthYear]: value,
        },
      }
    },
    {}
  )
  const atLeastOneNotExpected = useMemo(() => {
    return Object.values(notExpectedState).some((monthYearValues) =>
      Object.values(monthYearValues).some((value) => value === true)
    )
  }, [notExpectedState])

  const statements = useReselector(selectUserStatements)
  const skipUploadStatements = searchParams.get('skip') === 'true'
  const uploadStatementsActionItemStatus = skipUploadStatements
    ? UploadPrevMonthStatementStatus.skipped
    : UploadPrevMonthStatementStatus.uploaded

  const handleSaveLbaInterest = useCallback(async () => {
    if (currentUser?.financialProfile) {
      await dispatch(
        updateFinancialProfile(currentUser.financialProfile.id, {
          limitedBankAccessInterest: isInterestedInLba ? true : false,
        })
      )
    }
  }, [currentUser, isInterestedInLba, dispatch])

  useEffect(() => {
    const fetchStatuses = async () => {
      const statusesResponse = await dispatch(getAccountUploadStatuses())
      if (statusesResponse) {
        setStatuses(statusesResponse)
      }
    }
    fetchStatuses()
    dispatch(fetchUserDocuments())
    dispatch(fetchFinancialAccountsIfNeeded())
    dispatch(fetchPlaidItemsIfNeeded())
  }, [dispatch])

  useEffect(() => {
    pageView('upload monthly bank statements')
  }, [pageView])

  const plaidItems = useReselector(selectPlaidItems)

  const filteredPlaidItems = useMemo(() => {
    if (!statuses) return []
    const plaidItemArray = Object.values(plaidItems)
    const filteredItems = plaidItemArray.map((item) => {
      const accounts = item.accounts.filter((account) =>
        statuses.some(
          (status) =>
            status.id === account.id &&
            status.status === StatementStatusType.NO_SUPPORT
        )
      )
      return {
        ...item,
        accounts,
      }
    })
    return filteredItems.filter((item) => item.accounts.length > 0)
  }, [plaidItems, statuses])

  const uploadStatus = useMemo(() => {
    if (!statuses) return 'none'
    const allStatements = statuses.every(
      (status) => status.status === StatementStatusType.PLAID_STATEMENT
    )
    if (allStatements) return StatementStatusType.PLAID_STATEMENT
    const allLBA = statuses.every(
      (status) => status.status === StatementStatusType.LIMITED_BANK_ACCESS
    )
    if (allLBA) return 'all_lba'
    const someStatementsAndLBA = statuses.every(
      (status) =>
        status.status === StatementStatusType.LIMITED_BANK_ACCESS ||
        status.status === StatementStatusType.PLAID_STATEMENT
    )
    if (someStatementsAndLBA) return 'some_statement_lba'
    const noLBA = statuses.every(
      (status) => status.status === StatementStatusType.NO_SUPPORT
    )
    if (noLBA) return 'no_lba'
    const someLBA = statuses.some(
      (status) => status.status === StatementStatusType.LIMITED_BANK_ACCESS
    )
    if (someLBA) return 'some_lba'
    return 'none'
  }, [statuses])

  const step = useMemo(() => {
    return searchParams.get('step')
  }, [searchParams])

  const handleLbaIntroContinue = async () => {
    await handleSaveLbaInterest()
    navigate('/onboarding-v2?stage=statements&step=lbaInterest')
  }

  const handleNoLbaInterestContinue = async () => {
    await handleSaveLbaInterest()
    navigate('/onboarding-v2?stage=statements&step=noLbaIntro')
  }

  const handleLbaInterestWithUnsupportedInstitutionsContinue = async () => {
    await handleSaveLbaInterest()
    navigate('/onboarding-v2?stage=statements&step=unsupportedInstitutions')
  }

  const handleUnsupportedInstitutionsContinue = async () => {
    await markUserActionItemCompleteIfExists(
      OnboardingTaskIdents.UPLOAD_PREV_MONTH_STATEMENTS,
      undefined,
      uploadStatementsActionItemStatus
    )
    navigate(
      `/onboarding-v2?stage=statements&step=review&skip=${skipUploadStatements}`
    )
  }

  const { markComplete: completeBankStatementItem } =
    useCompleteOnboardingStepIfNeeded(
      GettingStartedOnboardingStep.uploadBankStatements
    )

  const handleNoLbaIntroContinue = async () => {
    await completeBankStatementItem()
    await markUserActionItemCompleteIfExists(
      OnboardingTaskIdents.UPLOAD_PREV_MONTH_STATEMENTS,
      undefined,
      uploadStatementsActionItemStatus
    )
    Object.entries(notExpectedState).forEach(
      ([financialAccountId, monthYearValues]) => {
        Object.entries(monthYearValues).forEach(async ([monthYear, value]) => {
          await markStatementMonthAsNotExpected({
            financialAccountId: Number(financialAccountId),
            statementMonth: monthYear,
            notExpected: value,
          })(dispatch)
        })
      }
    )
    if (fromGettingStarted) {
      navigate('/dashboard')
      return
    }
    navigate('/onboarding-v2?stage=statements&step=review')
    track('completed upload monthly bank statements page')
  }

  const handleLbaInterestContinue = () => {
    if (filteredPlaidItems.length > 0) {
      navigate('/onboarding-v2?stage=statements&step=noLbaIntro')
    } else {
      track('completed onboarding item upload statements', {
        status: 'done',
      })
      navigate(ONBOARDING_V2_ROUTE_NAMES.ONBOARDING_COMPLETE)
    }
  }

  const handlePlaidAccessContinue = () => {
    track('completed onboarding item upload statements', {
      status: 'done',
    })
    navigate(ONBOARDING_V2_ROUTE_NAMES.ONBOARDING_COMPLETE)
  }

  const handleReviewContinue = async () => {
    await markUserActionItemCompleteIfExists(
      OnboardingTaskIdents.UPLOAD_PREV_MONTH_STATEMENTS,
      undefined,
      uploadStatementsActionItemStatus
    )
    track('completed onboarding item upload statements', {
      status: 'done',
    })
    navigate(ONBOARDING_V2_ROUTE_NAMES.ONBOARDING_COMPLETE)
  }

  const handleStartContinue = () => {
    let nextStep = 'noLbaIntro'

    if (uploadStatus === 'plaid_statement') {
      nextStep = 'plaidAccess'
    }
    navigate(`/onboarding-v2?stage=statements&step=${nextStep}`)
  }

  const handleContinue = () => {
    const step = searchParams.get('step')

    if (step === 'start') {
      handleStartContinue()
      track('completed statements splash page')
    }

    if (step === 'lbaIntro') {
      if (!isInterestedInLba) {
        handleNoLbaInterestContinue()
      } else {
        handleLbaIntroContinue()
      }
      track('completed lba interest page')
    }

    if (step === 'noLbaIntro') {
      handleNoLbaIntroContinue()
    }

    if (step === 'lbaInterest') {
      if (filteredPlaidItems.length > 0) {
        handleLbaInterestWithUnsupportedInstitutionsContinue()
      } else {
        handleLbaInterestContinue()
      }
      track('completed lba interst confirmation page')
    }

    if (step === 'unsupportedInstitutions') {
      handleUnsupportedInstitutionsContinue()
    }

    if (step === 'noLbaInterest') {
      handleNoLbaInterestContinue()
    }

    if (step === 'plaidAccess') {
      handlePlaidAccessContinue()
      track('completed plaid statements page')
    }

    if (step === 'review') {
      handleReviewContinue()
    }
  }

  const component = useMemo(() => {
    switch (step) {
      case 'lbaIntro':
        return (
          <LBAIntroPage
            isInterestedInLba={isInterestedInLba}
            setIsInterestedInLba={setIsInterestedInLba}
          />
        )
      case 'lbaInterest':
        return (
          <LBAInterestConfirmationPage
            filteredPlaidItems={filteredPlaidItems}
          />
        )
      case 'noLbaIntro':
        return (
          <NoLBAIntroPage
            setShowSkipButton={setShowSkipButton}
            notExpectedState={notExpectedState}
            setNotExpectedValue={setNotExpectedValue}
          />
        )
      case 'plaidAccess':
        return <PlaidIntroPage />
      case 'noLBAInstitution':
        return <NoLBAInstitutionCard filteredPlaidItems={filteredPlaidItems} />
      case 'review':
        return <ReviewStatements />
      case 'unsupportedInstitutions':
        return <NoLBAInstitutionCard filteredPlaidItems={filteredPlaidItems} />
      default:
        return <IntroPage />
    }
  }, [
    step,
    isInterestedInLba,
    setIsInterestedInLba,
    filteredPlaidItems,
    notExpectedState,
    setNotExpectedValue,
  ])

  const handleBack = () => {
    if (fromGettingStarted) {
      navigate('/dashboard')
      return
    }

    if (step === 'start' || step === 'plaidAccess') {
      navigate('/onboarding-v2?stage=account&step=complete')
      return
    }

    let prevStep = 'step=start'

    if (
      (step === 'noLbaIntro' && uploadStatus === 'no_lba') ||
      step === 'lbaIntro'
    ) {
      prevStep = 'step=start'
    } else if (step === 'lbaInterest') {
      prevStep = 'step=lbaIntro&interested=true'
    } else if (step === 'unsupportedInstitutions') {
      prevStep = 'step=lbaInterest'
    } else if (step === 'review' && filteredPlaidItems.length > 0) {
      prevStep = `step=unsupportedInstitutions&skip=${skipUploadStatements}`
    } else if (step === 'review') {
      prevStep = 'step=noLbaIntro&skip=false'
    }

    navigate(`/onboarding-v2?stage=statements&${prevStep}`)
  }

  const loadingStatuses = useReselector(
    getIsFetching,
    ACCOUNT_UPLOAD_STATUSES_KEY
  )

  const continueDisabled = useMemo(() => {
    if (step === 'lbaIntro') {
      return isNil(isInterestedInLba)
    } else if (step === 'noLbaIntro') {
      setContinueDisabledMessage('At least one statement must be uploaded')
      return statements.length === 0 && !atLeastOneNotExpected
    }
    return loadingStatuses
  }, [
    step,
    loadingStatuses,
    isInterestedInLba,
    statements,
    atLeastOneNotExpected,
  ])

  const handleSkipClick = () => {
    toggleSkipModal()
  }

  const isSkipDisabled = useMemo(() => {
    return SKIPPABLE_STEPS.includes(step || '')
  }, [step])

  const finalStep = useMemo(() => {
    //if user is interested in lba but still has plaid items to review then this is not the final step
    if (step === 'lbaInterest' && filteredPlaidItems.length > 0) {
      return false
    }
    return ['lbaInterest', 'review', 'plaidAccess'].includes(step ?? '')
  }, [step, filteredPlaidItems.length])

  const completeButtonContent = useMemo(() => {
    return finalStep ? 'Complete' : ''
  }, [finalStep])

  return (
    <SetupCard
      onBackClick={handleBack}
      currentStep={finalStep ? 2 : 1}
      continueDisabled={continueDisabled}
      onContinueClick={handleContinue}
      showSkipButton={showSkipButton}
      onSkipClick={isSkipDisabled ? handleSkipClick : undefined}
      completeButtonContent={completeButtonContent}
      continueDisabledMessage={continueDisabledMessage}
      step={step ?? ''}
    >
      {component}
      {showSkipModal && (
        <SkipUploadStatementsModal closeModal={toggleSkipModal} />
      )}
    </SetupCard>
  )
}
